import React, { useEffect, useRef, useState } from 'react';

import { IContentfulImage } from '../../../interfaces/contentful-image.interface';
import LoginRegister from '../../user-profile/login-register';
import { DynamicOfferRedemption } from './dynamic-offer-redemption';
import { reservationPayload, Slot } from './interface';
import { getSlots, makeReservation } from './offer-redemption-api-helper';
import { OfferRedemptionErrorScreen } from './offer-redemption-error-screen';
import { StaticOfferRedemption } from './static-offer-redemption';
import { decryptQuery } from '@/helpers/encode-decode';
import { useUser } from '@/providers/UserProvider';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';

interface SpecialOfferRedemtionProps {
  offerRedemptionDetails?: {
    internalName: string;
    richText: any;
  };
  offerRedemptionMechanism?: string;
  handleClose: () => void;
  isQRCodeScanned: boolean;
  scannedText?: string;
  contentful_id?: string;
  tenantDetails: {
    offerName?: string;
    tenantName?: string;
    tenantLogo?: IContentfulImage;
  };
  node_locale: string;
  meetingPlaceHomePage?: any;
  offerBookingData: any;
}

export const SpecialOfferRedemtion = ({
  offerRedemptionDetails,
  offerRedemptionMechanism,
  handleClose,
  isQRCodeScanned,
  scannedText,
  contentful_id,
  tenantDetails,
  node_locale,
  meetingPlaceHomePage,
  offerBookingData,
}: SpecialOfferRedemtionProps) => {
  const [activeResource, setActiveResource] = useState<any>();
  const [activeSlot, setActiveSlot] = useState<Slot[]>();
  const [reservationSuccessfull, setReservationSuccessfull] = useState(false);
  const [reservationFailed, setReservationFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const { user } = useUser();

  const dictionary = useGetLibraryConstantsDictionaryData();
  const config = useTenantConfig();

  const dictionaryRef = useRef(dictionary);

  useEffect(() => {
    dictionaryRef.current = dictionary;
  }, [dictionary]);

  useEffect(() => {
    if (scannedText === 'CAMERA_PERMISSION_DENIED') {
      setReservationFailed(true);
      setErrorMessage(
        dictionaryRef?.current?.cameraPermisssionDenied ??
          'Camera permission denied. Please enable camera access.',
      );
      setLoading(false);
      return;
    }

    if (scannedText === 'CAMERA_ERROR') {
      setReservationFailed(true);
      setErrorMessage('Unable to access the camera. Please try again.');
      setLoading(false);
      return;
    }

    // Invalid redemption mechanism
    if (!offerRedemptionMechanism) {
      setReservationFailed(true);
      setErrorMessage(
        dictionaryRef.current?.invalidOfferRedemptionMechanism ?? '',
      );
      setLoading(false);
      return;
    }

    // Dynamic offer without booking details
    if (
      offerRedemptionMechanism === 'Dynamic' &&
      !offerBookingData?.some((item: any) => item?.bookingDetails)
    ) {
      setReservationFailed(true);
      setErrorMessage(dictionaryRef.current?.noBookingDetails ?? '');
      setLoading(false);
      return;
    }

    if (
      offerBookingData?.length > 0 &&
      scannedText &&
      scannedText?.length > 0 &&
      errorMessage?.length == 0 &&
      offerRedemptionMechanism === 'Dynamic'
    ) {
      const tenantId = decryptQuery(scannedText);
      // decryption failed or empty
      if (!tenantId || tenantId.length === 0) {
        setReservationFailed(true);
        setErrorMessage(
          dictionaryRef.current?.wrongQrCode ?? 'Invalid QR Code',
        );
        setLoading(false);
        return;
      }
      const filteredResource = offerBookingData.filter(
        (it: any) =>
          it?.bookingDetails?.properties?.tenant_id?.includes(tenantId) &&
          it?.bookingDetails?.properties?.cms_id == contentful_id,
      );
      if (filteredResource?.length == 0) {
        setReservationFailed(true);
        setErrorMessage(
          dictionaryRef?.current?.wrongQrCode ?? 'No Resource Available',
        );
        setLoading(false);
        return;
      }
      setActiveResource(filteredResource);
    } else {
      setLoading(false);
    }
  }, [offerBookingData, scannedText]);

  useEffect(() => {
    if (
      activeResource &&
      activeResource?.length > 0 &&
      offerRedemptionMechanism === 'Dynamic'
    ) {
      const getSlotsAPI = async () => {
        try {
          const response = await getSlots({
            meetingPlaceId: meetingPlaceHomePage?.businessUnitCode,
            lang: node_locale,
            id: activeResource[0]?.bookingDetails?.id,
          });
          if (!response || response.length === 0) {
            setReservationFailed(true);
            setErrorMessage(dictionaryRef.current?.noSlotsAvailable ?? '');
            setLoading(false);
            return;
          }
          setActiveSlot(response);
        } catch (err: any) {
          setReservationFailed(true);
          setErrorMessage(err?.error);

          console.error('Failed to get Resource', err);
        } finally {
          setLoading(false);
        }
      };

      getSlotsAPI();
    }
  }, [activeResource]);

  useEffect(() => {
    if (
      activeSlot &&
      activeSlot.length > 0 &&
      offerRedemptionMechanism === 'Dynamic'
    ) {
      let datePart = '';
      let timePart = '';

      const text = activeSlot && activeSlot[0] && activeSlot[0].text;
      if (text) {
        [datePart, timePart] = text.split(' ');
      }
      const originalDate = timePart
        ? new Date(`${datePart}T${timePart}:00Z`)
        : new Date(`${datePart}T00:00:00Z`);
      const updatedDate = new Date(originalDate);
      updatedDate.setUTCHours(
        updatedDate.getUTCHours() +
          Number(activeResource[0]?.bookingDetails?.max_rental_time),
      );

      const formattedOriginalDate = originalDate?.toISOString().slice(0, 16);
      const formattedUpdatedDate = updatedDate?.toISOString().slice(0, 16);

      const payload = {
        ...reservationPayload,
        resourceId: activeResource[0]?.bookingDetails?.id,
        startTime: formattedOriginalDate,
        endTime: formattedUpdatedDate,
        email: user?.email,
        firstName: user?.firstName,
        lastName: user?.lastName,
        phoneNumber: user?.phoneNumber,
        language: node_locale,
        country: meetingPlaceHomePage?.country?.countryCode,
      };
      const makeReservations = async () => {
        try {
          setLoading(true);
          setReservationFailed(false);
          setReservationSuccessfull(false);
          const response: any = await makeReservation(
            payload,
            {
              meetingPlaceId: meetingPlaceHomePage?.businessUnitCode,
            },
            {
              'X-Current-Language': node_locale,
              // 'X-ICIM-JWT': user_token ? user_token : '',
            },
          );
          if (response?.reservation_id) {
            setReservationSuccessfull(true);
            setReservationFailed(false);
          } else {
            setReservationSuccessfull(false);
            setReservationFailed(true);
            setErrorMessage(response?.response_message);
          }
        } catch (err: any) {
          setReservationSuccessfull(false);
          setReservationFailed(true);
          setErrorMessage(err?.error);

          console.error('Failed to get Resource', err);
        } finally {
          setLoading(false);
        }
      };

      makeReservations();
    }
  }, [activeSlot]);

  if (!user) {
    return (
      <LoginRegister
        node_locale={node_locale}
        brandId={meetingPlaceHomePage?.businessUnitCode ?? ''}
        config={config}
        handleClose={handleClose}
      />
    );
  }

  // null mechanism — show error immediately, no loading needed
  if (!offerRedemptionMechanism) {
    return (
      <OfferRedemptionErrorScreen
        contentful_id={contentful_id}
        handleClose={handleClose}
        tenantDetails={tenantDetails}
        errorMessage={errorMessage}
        node_locale={node_locale}
      />
    );
  }

  if (offerRedemptionMechanism === 'Static') {
    return (
      <StaticOfferRedemption
        contentful_id={contentful_id}
        offerRedemptionDetails={offerRedemptionDetails}
        tenantDetails={tenantDetails}
        handleClose={handleClose}
        node_locale={node_locale}
      />
    );
  }

  if (offerRedemptionMechanism === 'Dynamic') {
    if (loading) {
      return (
        <>
          <div
            role="status"
            className="absolute md:top-[45%] left-[45%] top-[100%]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 102 102"
              className="h-12 w-12 animate-spin fill-brand-yellow-1"
            >
              <path
                d="M96.57 39.43c6.31 25.75-9.46 51.74-35.21 58.05S9.63 88.02 3.32 62.27c-.91-3.72-1.37-7.53-1.38-11.36a47.85 47.85 0 0 1 13.57-33.44c4.63-4.75 12.14.83 7.32 6.46-6.46 7.21-11.59 15.87-11.59 26.31 0 22.48 18.22 40.7 40.7 40.7s40.7-18.22 40.7-40.7c0-3.23-.39-6.46-1.15-9.6-.8-3.66 4.1-4.89 5.08-1.21Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        </>
      );
    }
    if (isQRCodeScanned && reservationSuccessfull) {
      return (
        <DynamicOfferRedemption
          offerRedemptionDetails={
            reservationSuccessfull ? offerRedemptionDetails : undefined
          }
          tenantDetails={tenantDetails}
          contentful_id={contentful_id}
          handleClose={handleClose}
          node_locale={node_locale}
        />
      );
    }
    if (isQRCodeScanned && reservationFailed && !loading) {
      return (
        <OfferRedemptionErrorScreen
          contentful_id={contentful_id}
          handleClose={handleClose}
          tenantDetails={tenantDetails}
          errorMessage={errorMessage}
          node_locale={node_locale}
        />
      );
    }
    return null;
  }

  return null;
};
